import React, { FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonsContainer } from 'record-it-components/button/ButtonsContainer/ButtonsContainer';
import { prepareProps } from 'record-it-components/utils/propUtils';
import {
  ButtonStandard,
  ButtonStandardType
} from 'record-it-components/button/ButtonStandard/ButtonStandard';
import {
  ExpandableInlineCreationForm,
  ExpandableInlineCreationFormProps
} from 'record-it-components/form/ExpandableInlineCreationForm/ExpandableInlineCreationForm';
import { FormGroup } from 'record-it-components/form/FormGroup/FormGroup';
import { DateTimeInput } from 'record-it-components/dateTime/DateTimeInput/DateTimeInput';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { useOpened } from 'record-it-components/hooks/useOpened/useOpened';
import { useEventCallback } from 'record-it-components/hooks/useEventCallback';
import { useRequiredWorkTimeEntryToCreate } from '../../../hooks/createWorkTimeEntry/useRequiredWorkTimeEntryToCreate';
import { useValidation } from '../../../hooks/validation/useValidation';
import {
  MessageBox,
  MessageBoxType
} from 'record-it-components/misc/MessageBox/MessageBox';
import styles from '../../page/CreateWorkTimeEntryPage/CreateWorkTimeEntryPage.module.css';
import { validateBreak } from '../../../hooks/validation/functions/validateBreak';

export type CreateTimeEntryBreakInlineProps = PropsWithoutChildren<
  Omit<
    ExpandableInlineCreationFormProps,
    | 'children'
    | 'label'
    | 'opened'
    | 'buttons'
    | 'notOpenedContent'
    | 'openedContent'
  >
> & {
  /**
   * Start date a new break is initially set to.
   */
  initialStartDate?: Date;
  /**
   * End date a new break is initially set to.
   */
  initialEndDate?: Date;
  onCreate: (timeEntryBreak: TimeEntryBreakToCreate) => void;
};

export type TimeEntryBreakToCreate = {
  startDate: string;
  endDate: string;
};

export const CreateTimeEntryBreakInline: FunctionComponent<CreateTimeEntryBreakInlineProps> =
  React.memo((props) => {
    const { onCreate, initialStartDate, initialEndDate, ...remainingProps } =
      prepareProps({
        props,
        ignoreChildren: true
      });

    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(new Date());

    const { opened, close, open } = useOpened();

    const { t } = useTranslation();

    const workTimeEntry = useRequiredWorkTimeEntryToCreate();
    const canCreateBreakValidation = useValidation(
      validateBreak(
        { startDate: startDate.toISOString(), endDate: endDate.toISOString() },
        workTimeEntry
      ),
      [startDate, endDate, workTimeEntry]
    );

    const handleStartDateInput = useEventCallback((newDate: Date | null) => {
      if (newDate) {
        setStartDate(newDate);
      }
    });

    const handleEndDateInput = useEventCallback((newDate: Date | null) => {
      if (newDate) {
        setEndDate(newDate);
      }
    });

    const handleStartCreatingClick = useEventCallback(() => {
      const date = new Date();
      date.setSeconds(0);
      date.setMilliseconds(0);

      setStartDate(initialStartDate ?? new Date(date));
      setEndDate(initialEndDate ?? new Date(date));
      open();
    });

    const handleCreateClick = useEventCallback(() => {
      onCreate({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      });
      close();
    });

    return (
      <ExpandableInlineCreationForm
        {...remainingProps}
        label={t('components.timeTracking.CreateTimeEntryBreakInline.label')}
        opened={opened}
        openedContent={
          <>
            {!canCreateBreakValidation.valid ? (
              <MessageBox
                type={MessageBoxType.Error}
                className={styles.ErrorMessageBox}
                data-testid="CreateWorkTimeEntryPage.ErrorMessageBox"
              >
                {canCreateBreakValidation.errors.map((errorText) => {
                  return <div key={errorText}>{errorText}</div>;
                })}
              </MessageBox>
            ) : undefined}

            <FormGroup>
              <DateTimeInput
                label={t('entities.WorkTimeEntry.WorkTimeEntryBreak.startDate')}
                value={startDate}
                onInput={handleStartDateInput}
                data-testid="CreateTimeEntryBreakInline.StartDateInput"
              ></DateTimeInput>
            </FormGroup>

            <FormGroup>
              <DateTimeInput
                label={t('entities.WorkTimeEntry.WorkTimeEntryBreak.endDate')}
                value={endDate}
                onInput={handleEndDateInput}
                data-testid="CreateTimeEntryBreakInline.EndDateInput"
              ></DateTimeInput>
            </FormGroup>
          </>
        }
        buttons={
          <ButtonsContainer>
            <ButtonStandard
              type={ButtonStandardType.Primary}
              onClick={handleCreateClick}
              disabled={!canCreateBreakValidation.valid}
              data-testid="CreateTimeEntryBreakInline.CreateButton"
            >
              {t('components.timeTracking.CreateTimeEntryBreakInline.create')}
            </ButtonStandard>
            <ButtonStandard
              type={ButtonStandardType.Primary}
              onClick={close}
              data-testid="CreateTimeEntryBreakInline.CancelButton"
            >
              {t('components.timeTracking.CreateTimeEntryBreakInline.cancel')}
            </ButtonStandard>
          </ButtonsContainer>
        }
        notOpenedContent={
          <ButtonsContainer>
            <ButtonStandard
              type={ButtonStandardType.Primary}
              onClick={handleStartCreatingClick}
              data-testid="CreateTimeEntryBreakInline.StartCreatingButton"
            >
              {t(
                'components.timeTracking.CreateTimeEntryBreakInline.startCreatingBreak'
              )}
            </ButtonStandard>
          </ButtonsContainer>
        }
      ></ExpandableInlineCreationForm>
    );
  });
